import { useMelwaysLink } from '@seek/melways-react';
import { getHostName } from '@seek/melways-sites';
import { useTranslations } from '@vocab/react';
import {
  Badge,
  Box,
  ButtonLink,
  Column,
  Columns,
  IconEdit,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
  Text,
  TextLinkButton,
  useResponsiveValue,
} from 'braid-design-system';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  useNavigate,
  useParams,
  useRouteLoaderData,
  useSearchParams,
} from 'react-router';

import { buildWriteAReviewURI, CompanyProfileSummary } from '@seek/libs-shared';
import { CompanyProfilesPage, ROOT_PATH } from '../../../paths';
import { MainLayout } from '../mainLayout/MainLayout';

import {
  CompanyBrandingSection,
  useAppContext,
  useConfig,
  type CompanyBrandingSectionModel,
} from '@seek/libs-shared';
import type { AppConfig } from '../../../shared/config';
import { useFeatureFlags } from '../../../shared/providers/featureFlagProvider';
import { trackLinkEvents } from '../../../shared/tracking/candidateApp/trackLinkEvents';
import { getLocationContextualName } from '../../../shared/utils/getLocationContextualName';
import { CompanySearchBar } from '../../components/companySearchBar/CompanySearchBar';
import { getMetaData } from '../../components/MetaTag/meta';
import { MetaTag } from '../../components/MetaTag/MetaTag';
import { AboutPageContainer } from '../../pages/about/AboutPageContainer';
import { CulturePageContainer } from '../../pages/culture/CulturePageContainer';
import { JobsPageContainer } from '../../pages/jobs/JobsPageContainer';
import { ReviewsPageContainer } from '../../pages/reviews/ReviewsPageContainer';

import { SalariesPageContainer } from '../../pages/salaries/SalariesPageContainer';
import translations from './.vocab';

import * as styles from './CompanyProfileLayout.css.ts';

type Props = React.PropsWithChildren<{
  page: CompanyProfilesPage;
}>;

export function CompanyProfileLayout({ page }: Props) {
  const DEFAULT_TAB = CompanyProfilesPage.About;
  const { t } = useTranslations(translations);

  const companyProfileModel = useRouteLoaderData(
    'company-profile-root',
  ) as CompanyBrandingSectionModel;

  if (!companyProfileModel) {
    throw new Error(
      'Company profile model is not available. Please check the loader.',
    );
  }

  const [activeTab, setActiveTab] = React.useState<string | undefined>(page);
  const [showReviewReplyNotice, setShowReviewReplyNotice] =
    React.useState<boolean>(false);
  const [isDraft, setIsDraft] = useState(false);
  const [shouldShowBackToJobSearchLink, setShouldShowBackToJobSearchLink] =
    useState<boolean>(false);

  const config = useConfig<AppConfig>();
  const {
    brand,
    language,
    siteName: site,
    environment,
    absoluteUrl,
    consolidated,
  } = config;

  const navigate = useNavigate();
  const { companySlug, locationSlug } = useParams();
  const [params] = useSearchParams();
  const { country } = useConfig();
  const context = useAppContext();
  const { setCompanyId, setCompanyName } = context;
  const localize = useMelwaysLink();

  const { cookieFeatureFlags } = useFeatureFlags();
  const shouldShowSalariesPage =
    (brand === 'seek' || brand === 'jobstreet') &&
    cookieFeatureFlags.COMPANY_PROFILES_SALARIES_PAGE;

  const locationContextualName = getLocationContextualName({
    locationSlug,
    config,
  });

  const getCompanyProfilesPageUrl = useCallback(
    (p: CompanyProfilesPage) => {
      const searchParams = isDraft ? '?draft=true' : '';
      const getPath = () => {
        switch (p) {
          case CompanyProfilesPage.Culture:
          case CompanyProfilesPage.Jobs:
          case CompanyProfilesPage.Salaries:
          case CompanyProfilesPage.Reviews:
            return `/${ROOT_PATH}/${companySlug}/${p}${searchParams}`;
          default:
            return `/${ROOT_PATH}/${companySlug}${searchParams}`;
        }
      };

      return localize({
        path: getPath(),
        language,
      });
    },
    [companySlug, isDraft, language, localize],
  );

  const getOrigin = ():
    | 'home'
    | 'about'
    | 'culture'
    | 'jobs'
    | 'salaries'
    | 'reviews' => {
    switch (page) {
      case CompanyProfilesPage.About:
        return 'about';
      case CompanyProfilesPage.Culture:
        return 'culture';
      case CompanyProfilesPage.Jobs:
        return 'jobs';
      case CompanyProfilesPage.Salaries:
        return 'salaries';
      case CompanyProfilesPage.Reviews:
        return 'reviews';
      case CompanyProfilesPage.Home:
      default:
        return 'home';
    }
  };

  const trackWriteAReviewClicked = () => {
    trackLinkEvents.trackWriteAReviewButtonClicked({
      config,
      context,
      actionOrigin: getOrigin(),
    });
  };
  const handleWriteAReviewClicked = async () => {
    trackWriteAReviewClicked();
    await new Promise((resolve) => setTimeout(resolve, 500));
  };

  const trackReviewsClicked = () => {
    trackLinkEvents.trackReviewsClicked({
      config,
      context,
      actionOrigin: 'about',
    });
  };

  const trackJobsPaginationClicked = trackLinkEvents.trackPaginationClicked({
    config,
    context,
    currentPageSection: 'jobs',
  });

  const trackReviewsPaginationClicked = trackLinkEvents.trackPaginationClicked({
    config,
    context,
    currentPageSection: 'reviews',
  });

  const trackPhotosPaginationClicked = trackLinkEvents.trackPaginationClicked({
    config,
    context,
    currentPageSection: 'culture',
  });

  const trackSortOrderChanged = trackLinkEvents.trackSortOrderChanged({
    config,
    context,
  });

  const trackSeeAllPhotosButtonClicked = () =>
    trackLinkEvents.trackSeeAllPhotosButtonClicked({
      config,
      context,
    });

  const trackWebsiteClicked = () =>
    trackLinkEvents.trackWebsiteClicked({
      config,
      context,
    });

  const trackSignInClicked = () => {
    trackLinkEvents.trackSignInClicked({
      config,
      context,
    });
  };

  const trackRegisterClicked = () => {
    trackLinkEvents.trackRegisterClicked({
      config,
      context,
    });
  };

  const trackRandomCompanyProfileLinkClicked = ({
    linkedCompanyId,
  }: {
    linkedCompanyId: string;
  }) => {
    trackLinkEvents.trackRandomCompanyProfileLinkClicked({
      config,
      context,
      linkedCompanyId,
    });
  };

  const responsiveValue = useResponsiveValue();
  const tabPanelsRef = useRef<HTMLDivElement | null>(null);
  const onActiveTabChanged = useCallback(
    (item: string | undefined) => {
      const newActiveTab = item ? (item as CompanyProfilesPage) : DEFAULT_TAB;

      setActiveTab(newActiveTab);
      void navigate(getCompanyProfilesPageUrl(newActiveTab), {
        replace: true,
      });

      const isMobile = responsiveValue({
        mobile: true,
        tablet: false,
      });

      if (tabPanelsRef?.current) {
        const offset = isMobile ? 60 : 172; // Top padding for scroll positioning – height of tabs, and height of logo section and tabs
        const top =
          tabPanelsRef?.current?.getBoundingClientRect()?.top +
          window?.scrollY -
          offset;
        window?.scrollTo({ top, behavior: 'smooth' });
      }
    },
    [DEFAULT_TAB, getCompanyProfilesPageUrl, navigate, responsiveValue],
  );

  useEffect(() => {
    if (companyProfileModel && setCompanyId && setCompanyName) {
      setCompanyId(companyProfileModel.id);
      setCompanyName(companyProfileModel.name);
      setIsDraft(params.get('draft') === 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyProfileModel, params]);

  useEffect(() => {
    if (params.get('ac')) {
      onActiveTabChanged(CompanyProfilesPage.Reviews);
      setShowReviewReplyNotice(true);
    }
  }, [params, onActiveTabChanged]);

  const brandUrl = getHostName({
    site,
    staging: environment === 'staging' || environment === 'development',
    ...(consolidated ? { alternative: 'subDomainRegion' } : {}),
  });

  useEffect(() => {
    const splitedUrls = document.referrer.split(brandUrl);
    setShouldShowBackToJobSearchLink(
      splitedUrls.length === 2 && splitedUrls[1].substring(0, 4) === '/job',
    );
  }, [brandUrl]);

  return (
    <MainLayout pageWidth="medium" showHeaderDivider>
      <Columns space="small" alignY="center" collapseBelow="tablet">
        <Column width="2/3">
          {shouldShowBackToJobSearchLink && (
            <Text tone="secondary">
              <TextLinkButton
                weight="weak"
                onClick={() => {
                  window.history.back();
                }}
              >
                &lt; {t('Back to job search')}
              </TextLinkButton>
            </Text>
          )}
        </Column>
        <Column width="1/3">
          <CompanySearchBar setActiveTab={setActiveTab} />
        </Column>
      </Columns>

      <CompanyBrandingSection
        model={{
          name: companyProfileModel.name,
          branding: companyProfileModel.branding,
          rating: companyProfileModel.rating,
        }}
      />

      <Box
        background="body"
        paddingY="xxsmall"
        className={styles.stickySummarySection}
      >
        <CompanyProfileSummary
          model={{
            name: companyProfileModel.name,
            branding: companyProfileModel.branding,
            rating: companyProfileModel.rating,
          }}
          trackWriteAReviewClicked={trackWriteAReviewClicked}
          navigateToReviewsTab={() => {
            trackLinkEvents.trackReviewsClicked({
              config,
              context,
              actionOrigin: getOrigin(),
            });
            onActiveTabChanged(CompanyProfilesPage.Reviews);
          }}
          navigateToAboutTab={() => {
            onActiveTabChanged(CompanyProfilesPage.About);
          }}
          aboutPageLink={getCompanyProfilesPageUrl(CompanyProfilesPage.About)}
        />
      </Box>

      <Box
        display="flex"
        justifyContent="center"
        background="body"
        padding="small"
        className={styles.fixedWriteAReviewButton}
      >
        <ButtonLink
          onClick={() => {
            void handleWriteAReviewClicked();
          }}
          target="_blank"
          href={buildWriteAReviewURI({
            country,
            language,
            environment,
            consolidated,
            params: companyProfileModel.name
              ? `cn=${encodeURIComponent(companyProfileModel.name)}&tracking=company-profile`
              : undefined,
          })}
          variant="solid"
          rel="nofollow"
          size="standard"
          tone="formAccent"
          icon={<IconEdit />}
        >
          {t('Write a review')}
        </ButtonLink>
      </Box>

      <TabsProvider
        id="company-profile"
        selectedItem={activeTab}
        onChange={(_index, item) => onActiveTabChanged(item)}
      >
        <MetaTag
          {...getMetaData(config, {
            tab: activeTab as CompanyProfilesPage,
            companyTitle: companyProfileModel.name,
            reviewCount: companyProfileModel?.rating?.count,
            companyProfileImage: companyProfileModel.branding?.logoImageUrl,
            companyProfileZone: companyProfileModel.zone,
            brandUrl,
            absoluteUrl,
            locationName: locationContextualName,
          })}
        />
        <Box
          background="body"
          paddingBottom="none"
          className={styles.stickyTabs}
        >
          <Tabs label="" divider="full" reserveHitArea={true}>
            <Tab item={CompanyProfilesPage.About}>{t('About')}</Tab>
            <Tab item={CompanyProfilesPage.Culture}>
              {t('Life and Culture')}
            </Tab>
            <Tab item={CompanyProfilesPage.Jobs}>{t('Jobs')}</Tab>
            {shouldShowSalariesPage && (
              <Tab
                item={CompanyProfilesPage.Salaries}
                badge={<Badge tone="positive">New</Badge>}
              >
                {t('Salaries')}
              </Tab>
            )}
            <Tab item={CompanyProfilesPage.Reviews}>{t('Reviews')}</Tab>
          </Tabs>
        </Box>
        <Box ref={tabPanelsRef}>
          <TabPanels renderInactivePanels={false}>
            <TabPanel item={CompanyProfilesPage.About}>
              <AboutPageContainer
                onActiveTabChanged={onActiveTabChanged}
                getCompanyProfilesPageUrl={getCompanyProfilesPageUrl}
                trackWriteAReviewClicked={trackWriteAReviewClicked}
                trackReviewsClicked={trackReviewsClicked}
                trackSeeAllPhotosButtonClicked={trackSeeAllPhotosButtonClicked}
                trackWebsiteClicked={trackWebsiteClicked}
                trackRandomCompanyProfileLinkClicked={
                  trackRandomCompanyProfileLinkClicked
                }
              />
            </TabPanel>
            <TabPanel item={CompanyProfilesPage.Culture}>
              <CulturePageContainer
                trackPhotosPaginationClicked={trackPhotosPaginationClicked}
              />
            </TabPanel>
            <TabPanel item={CompanyProfilesPage.Jobs}>
              <JobsPageContainer
                trackJobsPaginationClicked={trackJobsPaginationClicked}
              />
            </TabPanel>
            {shouldShowSalariesPage && (
              <TabPanel item={CompanyProfilesPage.Salaries}>
                <SalariesPageContainer
                  trackJobsPaginationClicked={trackJobsPaginationClicked}
                />
              </TabPanel>
            )}
            <TabPanel item={CompanyProfilesPage.Reviews}>
              <ReviewsPageContainer
                showReviewReplyNotice={showReviewReplyNotice}
                trackWriteAReviewClicked={trackWriteAReviewClicked}
                trackReviewsPaginationClicked={trackReviewsPaginationClicked}
                trackSortOrderChanged={trackSortOrderChanged}
                trackSignInClicked={trackSignInClicked}
                trackRegisterClicked={trackRegisterClicked}
              />
            </TabPanel>
          </TabPanels>
        </Box>
      </TabsProvider>
    </MainLayout>
  );
}
